// Pure helpers for the "Cases & Packing" tab: drag ids, drop → action mapping and error messages.

// Error codes returned by projects/api/project-bundles/ packing actions (projects/packing on the backend).
const KNOWN_ERROR_CODES = new Set([
  'unit_unusable',
  'unit_not_allocated',
  'unit_allocation_exceeded',
  'unit_planned_elsewhere',
  'equipment_other_project',
  'equipment_serialized',
  'quantity_invalid',
  'quantity_exceeded',
  'template_product_not_allowed',
  'template_limit_exceeded',
  'case_full',
  'too_big',
  'over_volume',
  'invalid_source',
  'case_unit_wrong_type',
  'case_unit_unusable',
  'case_unit_planned_elsewhere',
]);

/** Packing error code from an API error (`{code}` or a serializer error `{code: [..]}`). */
export const packingErrorCode = (err) => {
  const code = err?.response?.data?.code;
  return Array.isArray(code) ? code[0] : code;
};

/** Translated, user-friendly message for a failed packing request. */
export const packingErrorMessage = (t, err, fallbackKey = 'projects.cases.errorAssigning') => {
  const code = packingErrorCode(err);
  if (!KNOWN_ERROR_CODES.has(code)) return t(fallbackKey);
  // Extra fields of the error (e.g. remaining, room, allocated) are interpolated into the message.
  return t(`projects.cases.errors.${code}`, err.response.data);
};

export const unitDragId = (unitId) => `unit-${unitId}`;
export const quantityDragId = (projectEquipmentId) => `qty-${projectEquipmentId}`;
export const contentDragId = (contentId) => `content-${contentId}`;
export const bundleDropId = (bundleId) => `bundle-${bundleId}`;
export const LOOSE_DROP_ID = 'loose';

const findContent = (bundles, contentId) => {
  for (const bundle of bundles) {
    const content = (bundle.contents || []).find((c) => c.id === contentId);
    if (content) return { bundle, content };
  }
  return null;
};

const contentAction = (data, contentId, targetBundleId) => {
  const found = findContent(data.bundles || [], contentId);
  if (!found) return null;
  const { bundle, content } = found;
  const isQuantity = content.kind === 'quantity';
  if (targetBundleId === null) {
    return { type: 'unpack', bundleId: bundle.id, content: content.id, max: isQuantity ? content.quantity : null, name: content.product_name };
  }
  if (!isQuantity) return { type: 'packUnit', bundleId: targetBundleId, itemUnit: content.item_unit };
  return {
    type: 'packQuantity',
    bundleId: targetBundleId,
    projectEquipment: content.project_equipment,
    sourceContent: content.id,
    max: content.quantity,
    name: content.product_name,
  };
};

/**
 * Map a drag & drop result to a packing action, or null when nothing should happen.
 * Actions: packUnit {bundleId, itemUnit}, packQuantity {bundleId, projectEquipment, max, sourceContent?},
 * unpack {bundleId, content, max} — `max` is set when the user has to be asked for a quantity.
 */
export const resolveDrop = ({ draggableId, source, destination }, data) => {
  if (!destination || source.droppableId === destination.droppableId) return null;
  const targetBundleId = destination.droppableId === LOOSE_DROP_ID
    ? null
    : Number(destination.droppableId.replace('bundle-', ''));
  const [kind, rawId] = draggableId.split('-');
  const id = Number(rawId);
  if (kind === 'content') return contentAction(data, id, targetBundleId);
  if (targetBundleId === null) return null;
  if (kind === 'unit') return { type: 'packUnit', bundleId: targetBundleId, itemUnit: id };
  if (kind === 'qty') {
    const line = (data.loose_quantities || []).find((l) => l.project_equipment === id);
    if (!line) return null;
    return { type: 'packQuantity', bundleId: targetBundleId, projectEquipment: id, max: line.remaining, name: line.product_name };
  }
  return null;
};
